// ── La pestaña activa vive en la URL ─────────────────────────────────────────
// Antes cada pantalla la guardaba en useState: navegar afuera y volver con el
// botón del navegador devolvía SIEMPRE al inicio del módulo, perdiendo dónde
// estabas. Con la pestaña en ?tab= el historial la respeta sola.
//
// replace, no push: cambiar de pestaña NO agrega una entrada al historial —
// "volver" tiene que salir del módulo, no recorrer las pestañas que miraste.
// La entrada al historial la crea la navegación de verdad (abrir un detalle,
// ir a otro módulo), y esa sí recuerda su ?tab=.

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useTabParam<T extends string>(
  valid: readonly T[],
  fallback: T,
  /** Nombre del parámetro. Solo se cambia si la pantalla ya usa 'tab'. */
  key = 'tab',
): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const active = (valid as readonly string[]).includes(raw ?? '') ? (raw as T) : fallback;

  const setActive = useCallback((next: T) => {
    setParams(prev => {
      const p = new URLSearchParams(prev);
      // El valor por defecto no ensucia la URL: /contactos/5 y
      // /contactos/5?tab=info son la misma pantalla.
      if (next === fallback) p.delete(key);
      else p.set(key, next);
      return p;
    }, { replace: true });
  }, [setParams, fallback, key]);

  return [active, setActive];
}
